import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FileText,
  Plus,
  Trash2,
  TrendingUp,
  Info,
  AlertTriangle,
  CheckCircle,
  Award,
  Scale,
  Shield,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { scoreTermSheet } from '@/utils/equityMath';
import type { TermSheetTerms, TermSheetScore } from '@/utils/equityMath';

interface TermSheet extends TermSheetTerms {
  id: string;
  investorName: string;
}

/** Percentage of the company sold in the round, or null when the offer is blank. */
function dilutionOf(sheet: TermSheet): number | null {
  const postMoney = sheet.preMoney + sheet.investment;
  return postMoney > 0 ? (sheet.investment / postMoney) * 100 : null;
}

const pct = (value: number | null) => (value === null ? '—' : `${value.toFixed(1)}%`);

const PROTECTIVE_PROVISIONS_OPTIONS = [
  'Sale of company',
  'Liquidation or dissolution',
  'Amendment of charter/bylaws',
  'Changes to board size/composition',
  'Issuance of senior securities',
  'Dividend declarations',
  'Stock repurchases',
  'Material contracts (>$100k)',
  'Debt financing (>$500k)',
  'Intellectual property transfers',
  'Changes to option pool',
  'Affiliate transactions',
];

export function TermSheetComparison() {
  const [termSheets, setTermSheets] = useState<TermSheet[]>([
    {
      id: '1',
      investorName: 'Investor A',
      preMoney: 10000000,
      investment: 3000000,
      liquidationPrefMultiple: 1,
      participating: false,
      participationCap: null,
      antiDilution: 'broad-based',
      proRataRights: true,
      investorBoardSeats: 1,
      founderBoardSeats: 2,
      independentBoardSeats: 0,
      protectiveProvisions: ['Sale of company', 'Issuance of senior securities'],
      dividendPreference: false,
      redemptionRights: false,
      dragAlongRights: true,
      noShopPeriod: 30,
      exclusivityDays: 45,
      foundersVestingRequired: true,
      optionPoolSize: 15,
      payToPlay: false,
      informationRights: true,
      registrationRights: 'both',
    },
  ]);

  const addTermSheet = () => {
    const newSheet: TermSheet = {
      id: Date.now().toString(),
      investorName: `Investor ${String.fromCharCode(65 + termSheets.length)}`,
      preMoney: 0,
      investment: 0,
      liquidationPrefMultiple: 1,
      participating: false,
      participationCap: null,
      antiDilution: 'none',
      proRataRights: true,
      investorBoardSeats: 1,
      founderBoardSeats: 2,
      independentBoardSeats: 0,
      protectiveProvisions: [],
      dividendPreference: false,
      redemptionRights: false,
      dragAlongRights: true,
      noShopPeriod: 30,
      exclusivityDays: 0,
      foundersVestingRequired: false,
      optionPoolSize: 10,
      payToPlay: false,
      informationRights: true,
      registrationRights: 'piggyback',
    };
    setTermSheets([...termSheets, newSheet]);
  };

  const removeTermSheet = (id: string) => {
    setTermSheets(termSheets.filter((ts) => ts.id !== id));
  };

  const updateTermSheet = <K extends keyof TermSheet>(
    id: string,
    field: K,
    value: TermSheet[K]
  ) => {
    setTermSheets(termSheets.map((ts) => (ts.id === id ? { ...ts, [field]: value } : ts)));
  };

  // Scored one sheet at a time, so a sheet's grade never moves because a rival
  // offer was added or deleted.
  const scores = new Map<string, TermSheetScore>(
    termSheets.map((sheet) => [sheet.id, scoreTermSheet(sheet)])
  );
  const scoreOf = (sheet: TermSheet): number => scores.get(sheet.id)?.score ?? 0;

  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#3b82f6';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const getRankings = () => {
    return termSheets
      .map((sheet) => ({ sheet, score: scoreOf(sheet) }))
      .sort((a, b) => b.score - a.score);
  };

  const rankings = getRankings();
  const bestOfferId = rankings[0]?.sheet.id;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Term Sheet Comparison Tool
          </CardTitle>
          <CardDescription>
            Compare multiple investment offers side-by-side to identify the best terms for your
            company
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">
              Investment Offers ({termSheets.length})
            </Label>
            <Button onClick={addTermSheet} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Offer
            </Button>
          </div>

          <Tabs defaultValue="comparison" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="comparison">Side-by-Side</TabsTrigger>
              <TabsTrigger value="details">Detailed Terms</TabsTrigger>
              <TabsTrigger value="rankings">Rankings & Analysis</TabsTrigger>
            </TabsList>

            <TabsContent value="comparison" className="space-y-4 mt-4">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2">
                      <th className="p-3 text-left font-semibold sticky left-0 bg-background z-10 min-w-[200px]">
                        Term
                      </th>
                      {termSheets.map((sheet) => (
                        <th key={sheet.id} className="p-3 text-center font-semibold min-w-[180px]">
                          <div className="space-y-2">
                            <div className="font-bold">{sheet.investorName}</div>
                            <div className="flex items-center justify-center gap-2">
                              <Badge
                                style={{ backgroundColor: getScoreColor(scoreOf(sheet)) }}
                                className="text-xs"
                              >
                                Score: {scoreOf(sheet)}
                              </Badge>
                              <Button
                                onClick={() => removeTermSheet(sheet.id)}
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Valuation Section */}
                    <tr className="bg-muted/50">
                      <td
                        className="p-3 font-semibold sticky left-0 bg-muted/50 z-10"
                        colSpan={termSheets.length + 1}
                      >
                        💰 Valuation & Economics
                      </td>
                    </tr>
                    <ComparisonRow
                      label="Pre-Money Valuation"
                      sheets={termSheets}
                      getValue={(sheet) => `$${(sheet.preMoney / 1000000).toFixed(1)}M`}
                      isHighlighted={(sheet) =>
                        sheet.preMoney === Math.max(...termSheets.map((s) => s.preMoney))
                      }
                    />
                    <ComparisonRow
                      label="Investment Amount"
                      sheets={termSheets}
                      getValue={(sheet) => `$${(sheet.investment / 1000000).toFixed(1)}M`}
                    />
                    <ComparisonRow
                      label="Post-Money Valuation"
                      sheets={termSheets}
                      getValue={(sheet) =>
                        `$${((sheet.preMoney + sheet.investment) / 1000000).toFixed(1)}M`
                      }
                      isHighlighted={(sheet) =>
                        sheet.preMoney + sheet.investment ===
                        Math.max(...termSheets.map((s) => s.preMoney + s.investment))
                      }
                    />
                    <ComparisonRow
                      label="Dilution %"
                      sheets={termSheets}
                      getValue={(sheet) => pct(dilutionOf(sheet))}
                      isHighlighted={(sheet) => {
                        const known = termSheets
                          .map(dilutionOf)
                          .filter((d): d is number => d !== null);
                        const mine = dilutionOf(sheet);
                        return mine !== null && known.length > 0 && mine === Math.min(...known);
                      }}
                    />

                    {/* Preferences Section */}
                    <tr className="bg-muted/50">
                      <td
                        className="p-3 font-semibold sticky left-0 bg-muted/50 z-10"
                        colSpan={termSheets.length + 1}
                      >
                        🛡️ Liquidation Preferences
                      </td>
                    </tr>
                    <ComparisonRow
                      label="Preference Multiple"
                      sheets={termSheets}
                      getValue={(sheet) => `${sheet.liquidationPrefMultiple}x`}
                      isHighlighted={(sheet) =>
                        sheet.liquidationPrefMultiple ===
                        Math.min(...termSheets.map((s) => s.liquidationPrefMultiple))
                      }
                    />
                    <ComparisonRow
                      label="Participating"
                      sheets={termSheets}
                      getValue={(sheet) => (sheet.participating ? 'Yes' : 'No')}
                      isHighlighted={(sheet) => !sheet.participating}
                    />
                    <ComparisonRow
                      label="Participation Cap"
                      sheets={termSheets}
                      getValue={(sheet) =>
                        sheet.participationCap ? `${sheet.participationCap}x` : 'None'
                      }
                    />

                    {/* Protection Section */}
                    <tr className="bg-muted/50">
                      <td
                        className="p-3 font-semibold sticky left-0 bg-muted/50 z-10"
                        colSpan={termSheets.length + 1}
                      >
                        🔒 Anti-Dilution & Rights
                      </td>
                    </tr>
                    <ComparisonRow
                      label="Anti-Dilution"
                      sheets={termSheets}
                      getValue={(sheet) => {
                        const labels = {
                          none: 'None',
                          'broad-based': 'Broad Weighted Avg',
                          'narrow-based': 'Narrow Weighted Avg',
                          'full-ratchet': 'Full Ratchet',
                        };
                        return labels[sheet.antiDilution];
                      }}
                      isHighlighted={(sheet) => sheet.antiDilution === 'none'}
                    />
                    <ComparisonRow
                      label="Pro-Rata Rights"
                      sheets={termSheets}
                      getValue={(sheet) => (sheet.proRataRights ? 'Yes' : 'No')}
                      isHighlighted={(sheet) => sheet.proRataRights}
                    />
                    <ComparisonRow
                      label="Registration Rights"
                      sheets={termSheets}
                      getValue={(sheet) => {
                        const labels = {
                          demand: 'Demand',
                          piggyback: 'Piggyback',
                          both: 'Both',
                          none: 'None',
                        };
                        return labels[sheet.registrationRights];
                      }}
                    />

                    {/* Board Section */}
                    <tr className="bg-muted/50">
                      <td
                        className="p-3 font-semibold sticky left-0 bg-muted/50 z-10"
                        colSpan={termSheets.length + 1}
                      >
                        👥 Board Composition
                      </td>
                    </tr>
                    <ComparisonRow
                      label="Investor Seats"
                      sheets={termSheets}
                      getValue={(sheet) => sheet.investorBoardSeats.toString()}
                    />
                    <ComparisonRow
                      label="Founder Seats"
                      sheets={termSheets}
                      getValue={(sheet) => sheet.founderBoardSeats.toString()}
                    />
                    <ComparisonRow
                      label="Independent Seats"
                      sheets={termSheets}
                      getValue={(sheet) => sheet.independentBoardSeats.toString()}
                    />
                    <ComparisonRow
                      label="Founder Control"
                      sheets={termSheets}
                      getValue={(sheet) => {
                        const total =
                          sheet.investorBoardSeats +
                          sheet.founderBoardSeats +
                          sheet.independentBoardSeats;
                        return sheet.founderBoardSeats > total / 2 ? 'Yes' : 'No';
                      }}
                      isHighlighted={(sheet) => {
                        const total =
                          sheet.investorBoardSeats +
                          sheet.founderBoardSeats +
                          sheet.independentBoardSeats;
                        return sheet.founderBoardSeats > total / 2;
                      }}
                    />

                    {/* Other Terms */}
                    <tr className="bg-muted/50">
                      <td
                        className="p-3 font-semibold sticky left-0 bg-muted/50 z-10"
                        colSpan={termSheets.length + 1}
                      >
                        📋 Other Key Terms
                      </td>
                    </tr>
                    <ComparisonRow
                      label="Protective Provisions"
                      sheets={termSheets}
                      getValue={(sheet) => `${sheet.protectiveProvisions.length} items`}
                      isHighlighted={(sheet) =>
                        sheet.protectiveProvisions.length ===
                        Math.min(...termSheets.map((s) => s.protectiveProvisions.length))
                      }
                    />
                    <ComparisonRow
                      label="Pay-to-Play"
                      sheets={termSheets}
                      getValue={(sheet) => (sheet.payToPlay ? 'Yes' : 'No')}
                      isHighlighted={(sheet) => !sheet.payToPlay}
                    />
                    <ComparisonRow
                      label="Founder Vesting Required"
                      sheets={termSheets}
                      getValue={(sheet) => (sheet.foundersVestingRequired ? 'Yes' : 'No')}
                      isHighlighted={(sheet) => !sheet.foundersVestingRequired}
                    />
                    <ComparisonRow
                      label="Option Pool"
                      sheets={termSheets}
                      getValue={(sheet) => `${sheet.optionPoolSize}%`}
                    />
                    <ComparisonRow
                      label="No-Shop Period"
                      sheets={termSheets}
                      getValue={(sheet) => `${sheet.noShopPeriod} days`}
                      isHighlighted={(sheet) =>
                        sheet.noShopPeriod === Math.min(...termSheets.map((s) => s.noShopPeriod))
                      }
                    />
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="details" className="space-y-4 mt-4">
              {termSheets.map((sheet) => (
                <Card key={sheet.id} className="border-muted">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-base">{sheet.investorName}</CardTitle>
                        <Badge style={{ backgroundColor: getScoreColor(scoreOf(sheet)) }}>
                          Score: {scoreOf(sheet)}
                        </Badge>
                        {sheet.id === bestOfferId && termSheets.length > 1 && (
                          <Badge variant="outline" className="text-xs">
                            <Award className="h-3 w-3 mr-1" />
                            Best Offer
                          </Badge>
                        )}
                      </div>
                      <Button onClick={() => removeTermSheet(sheet.id)} size="sm" variant="ghost">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Basic Economics */}
                      <div className="space-y-3">
                        <Label className="text-sm font-semibold">💰 Valuation</Label>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs">Investor Name</Label>
                            <Input
                              value={sheet.investorName}
                              onChange={(e) =>
                                updateTermSheet(sheet.id, 'investorName', e.target.value)
                              }
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Pre-Money Valuation</Label>
                            <Input
                              type="number"
                              value={sheet.preMoney}
                              onChange={(e) =>
                                updateTermSheet(sheet.id, 'preMoney', Number(e.target.value))
                              }
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Investment Amount</Label>
                            <Input
                              type="number"
                              value={sheet.investment}
                              onChange={(e) =>
                                updateTermSheet(sheet.id, 'investment', Number(e.target.value))
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* Preferences */}
                      <div className="space-y-3">
                        <Label className="text-sm font-semibold">🛡️ Preferences</Label>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs">Liquidation Preference</Label>
                            <Select
                              value={sheet.liquidationPrefMultiple.toString()}
                              onValueChange={(v) =>
                                updateTermSheet(sheet.id, 'liquidationPrefMultiple', Number(v))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">1x</SelectItem>
                                <SelectItem value="1.5">1.5x</SelectItem>
                                <SelectItem value="2">2x</SelectItem>
                                <SelectItem value="3">3x</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={sheet.participating}
                              onCheckedChange={(checked) =>
                                updateTermSheet(sheet.id, 'participating', checked === true)
                              }
                            />
                            <Label className="text-xs">Participating Preferred</Label>
                          </div>
                          {sheet.participating && (
                            <div>
                              <Label className="text-xs">Participation Cap (multiple)</Label>
                              <Input
                                type="number"
                                value={sheet.participationCap || ''}
                                onChange={(e) =>
                                  updateTermSheet(
                                    sheet.id,
                                    'participationCap',
                                    e.target.value ? Number(e.target.value) : null
                                  )
                                }
                                placeholder="e.g., 3"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Anti-Dilution */}
                      <div className="space-y-3">
                        <Label className="text-sm font-semibold">🔒 Protection</Label>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs">Anti-Dilution</Label>
                            <Select
                              value={sheet.antiDilution}
                              onValueChange={(v: TermSheet['antiDilution']) =>
                                updateTermSheet(sheet.id, 'antiDilution', v)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="broad-based">
                                  Broad-Based Weighted Avg
                                </SelectItem>
                                <SelectItem value="narrow-based">
                                  Narrow-Based Weighted Avg
                                </SelectItem>
                                <SelectItem value="full-ratchet">Full Ratchet</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={sheet.proRataRights}
                              onCheckedChange={(checked) =>
                                updateTermSheet(sheet.id, 'proRataRights', checked === true)
                              }
                            />
                            <Label className="text-xs">Pro-Rata Rights</Label>
                          </div>
                          <div>
                            <Label className="text-xs">Registration Rights</Label>
                            <Select
                              value={sheet.registrationRights}
                              onValueChange={(v: TermSheet['registrationRights']) =>
                                updateTermSheet(sheet.id, 'registrationRights', v)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="piggyback">Piggyback Only</SelectItem>
                                <SelectItem value="demand">Demand Only</SelectItem>
                                <SelectItem value="both">Both</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Board */}
                      <div className="space-y-3">
                        <Label className="text-sm font-semibold">👥 Board</Label>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs">Investor Seats</Label>
                            <Input
                              type="number"
                              value={sheet.investorBoardSeats}
                              onChange={(e) =>
                                updateTermSheet(
                                  sheet.id,
                                  'investorBoardSeats',
                                  Number(e.target.value)
                                )
                              }
                              min="0"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Founder Seats</Label>
                            <Input
                              type="number"
                              value={sheet.founderBoardSeats}
                              onChange={(e) =>
                                updateTermSheet(
                                  sheet.id,
                                  'founderBoardSeats',
                                  Number(e.target.value)
                                )
                              }
                              min="0"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Independent Seats</Label>
                            <Input
                              type="number"
                              value={sheet.independentBoardSeats}
                              onChange={(e) =>
                                updateTermSheet(
                                  sheet.id,
                                  'independentBoardSeats',
                                  Number(e.target.value)
                                )
                              }
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Other Terms */}
                      <div className="space-y-3 col-span-2">
                        <Label className="text-sm font-semibold">📋 Other Terms</Label>
                        <div className="grid md:grid-cols-2 gap-3">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={sheet.payToPlay}
                              onCheckedChange={(checked) =>
                                updateTermSheet(sheet.id, 'payToPlay', checked === true)
                              }
                            />
                            <Label className="text-xs">Pay-to-Play Provision</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={sheet.foundersVestingRequired}
                              onCheckedChange={(checked) =>
                                updateTermSheet(
                                  sheet.id,
                                  'foundersVestingRequired',
                                  checked === true
                                )
                              }
                            />
                            <Label className="text-xs">Founder Vesting Required</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={sheet.dragAlongRights}
                              onCheckedChange={(checked) =>
                                updateTermSheet(sheet.id, 'dragAlongRights', checked === true)
                              }
                            />
                            <Label className="text-xs">Drag-Along Rights</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={sheet.redemptionRights}
                              onCheckedChange={(checked) =>
                                updateTermSheet(sheet.id, 'redemptionRights', checked === true)
                              }
                            />
                            <Label className="text-xs">Redemption Rights</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={sheet.dividendPreference}
                              onCheckedChange={(checked) =>
                                updateTermSheet(sheet.id, 'dividendPreference', checked === true)
                              }
                            />
                            <Label className="text-xs">Dividend Preference</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={sheet.informationRights}
                              onCheckedChange={(checked) =>
                                updateTermSheet(sheet.id, 'informationRights', checked === true)
                              }
                            />
                            <Label className="text-xs">Information Rights</Label>
                          </div>
                          <div>
                            <Label className="text-xs">Exclusivity (days)</Label>
                            <Input
                              type="number"
                              value={sheet.exclusivityDays}
                              onChange={(e) =>
                                updateTermSheet(sheet.id, 'exclusivityDays', Number(e.target.value))
                              }
                              min="0"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Option Pool Size (%)</Label>
                            <Input
                              type="number"
                              value={sheet.optionPoolSize}
                              onChange={(e) =>
                                updateTermSheet(sheet.id, 'optionPoolSize', Number(e.target.value))
                              }
                              min="0"
                              max="100"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">No-Shop Period (days)</Label>
                            <Input
                              type="number"
                              value={sheet.noShopPeriod}
                              onChange={(e) =>
                                updateTermSheet(sheet.id, 'noShopPeriod', Number(e.target.value))
                              }
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Protective Provisions */}
                      <div className="space-y-3 col-span-2">
                        <Label className="text-sm font-semibold">
                          ⚠️ Protective Provisions (Veto Rights)
                        </Label>
                        <div className="grid md:grid-cols-2 gap-2">
                          {PROTECTIVE_PROVISIONS_OPTIONS.map((provision) => (
                            <div key={provision} className="flex items-center space-x-2">
                              <Checkbox
                                checked={sheet.protectiveProvisions.includes(provision)}
                                onCheckedChange={(checked) => {
                                  const newProvisions = checked
                                    ? [...sheet.protectiveProvisions, provision]
                                    : sheet.protectiveProvisions.filter((p) => p !== provision);
                                  updateTermSheet(sheet.id, 'protectiveProvisions', newProvisions);
                                }}
                              />
                              <Label className="text-xs">{provision}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="rankings" className="space-y-4 mt-4">
              {/* Rankings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Scale className="h-4 w-4" />
                    Overall Rankings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {rankings.map((item, idx) => (
                    <Card key={item.sheet.id} className="border-muted">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-semibold">{item.sheet.investorName}</p>
                              <p className="text-xs text-muted-foreground">
                                ${(item.sheet.preMoney / 1000000).toFixed(1)}M pre-money
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              style={{ backgroundColor: getScoreColor(item.score) }}
                              className="text-sm px-3"
                            >
                              {item.score} / 100
                            </Badge>
                            {idx === 0 && <Award className="h-5 w-5 text-warning" />}
                          </div>
                        </div>
                        <Progress
                          value={item.score}
                          className="h-2"
                          style={{
                            // @ts-expect-error - CSS custom property not in React.CSSProperties
                            '--progress-background': getScoreColor(item.score),
                          }}
                        />
                        {(() => {
                          const detail = scores.get(item.sheet.id);
                          if (!detail) return null;
                          if (detail.deductions.length === 0) {
                            return (
                              <p className="text-xs text-muted-foreground mt-2">
                                Nothing unfavourable in this sheet.
                              </p>
                            );
                          }
                          return (
                            <div className="mt-3 space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                Points off (100 = nothing unfavourable):
                              </p>
                              {detail.deductions.map((d) => (
                                <div
                                  key={d.term}
                                  className="flex items-baseline justify-between gap-3 text-xs"
                                >
                                  <span className="text-muted-foreground">
                                    <span className="font-medium text-foreground">{d.term}</span> —{' '}
                                    {d.detail}
                                  </span>
                                  <span className="font-mono whitespace-nowrap">
                                    −{d.points.toFixed(1)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>

              {/* Key Insights */}
              <Card className="border-primary/50 bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Key Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {termSheets.length === 0 && (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        No offers to compare. Add one to see rankings and analysis.
                      </AlertDescription>
                    </Alert>
                  )}
                  {(() => {
                    const bestDeal = rankings[0]?.sheet;
                    // reduce() with no seed throws on an empty list, and every
                    // offer can be deleted.
                    const highestValuation = termSheets.reduce<TermSheet | null>(
                      (max, sheet) => (max === null || sheet.preMoney > max.preMoney ? sheet : max),
                      null
                    );
                    const bestControl = termSheets.find(
                      (sheet) => scores.get(sheet.id)?.founderBoardControl
                    );

                    return (
                      <>
                        {bestDeal && (
                          <Alert>
                            <CheckCircle className="h-4 w-4 text-success" />
                            <AlertDescription>
                              <strong>Best Overall:</strong> {bestDeal.investorName} offers the most
                              founder-friendly terms with a score of {scoreOf(bestDeal)}/100.
                            </AlertDescription>
                          </Alert>
                        )}

                        {highestValuation && highestValuation.id !== bestDeal?.id && (
                          <Alert>
                            <TrendingUp className="h-4 w-4" />
                            <AlertDescription>
                              <strong>Highest Valuation:</strong> {highestValuation.investorName}{' '}
                              offers ${(highestValuation.preMoney / 1000000).toFixed(1)}M pre-money.
                              Valuation is not part of the score, which grades the terms attached to
                              the money — compare the two.
                            </AlertDescription>
                          </Alert>
                        )}

                        {bestControl && (
                          <Alert>
                            <Shield className="h-4 w-4" />
                            <AlertDescription>
                              <strong>Best Control:</strong> {bestControl.investorName} allows
                              founders to maintain board control.
                            </AlertDescription>
                          </Alert>
                        )}

                        {termSheets.some((sheet) => sheet.participating) && (
                          <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                              <strong>Warning:</strong> Some offers include participating preferred
                              stock, which can significantly reduce founder returns in exit
                              scenarios.
                            </AlertDescription>
                          </Alert>
                        )}

                        {termSheets.some((sheet) => sheet.antiDilution === 'full-ratchet') && (
                          <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                              <strong>Critical:</strong> Full ratchet anti-dilution protection can
                              be extremely dilutive to founders in down rounds. This is generally
                              considered highly unfavorable.
                            </AlertDescription>
                          </Alert>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Recommendation */}
              {rankings[0] && (
                <Card className="border-success bg-success/5">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Award className="h-4 w-4 text-success" />
                      Recommendation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p>
                      Based on our analysis, <strong>{rankings[0].sheet.investorName}</strong>{' '}
                      offers the best overall terms for founders with a score of{' '}
                      <strong>{rankings[0].score}/100</strong>.
                    </p>
                    <ul className="list-disc ml-5 space-y-1 text-muted-foreground">
                      <li>
                        Valuation: ${(rankings[0].sheet.preMoney / 1000000).toFixed(1)}M pre-money
                        ($
                        {(
                          (rankings[0].sheet.preMoney + rankings[0].sheet.investment) /
                          1000000
                        ).toFixed(1)}
                        M post)
                      </li>
                      <li>
                        Liquidation: {rankings[0].sheet.liquidationPrefMultiple}x{' '}
                        {rankings[0].sheet.participating ? 'participating' : 'non-participating'}
                      </li>
                      <li>
                        Board Control:{' '}
                        {(() => {
                          const total =
                            rankings[0].sheet.investorBoardSeats +
                            rankings[0].sheet.founderBoardSeats +
                            rankings[0].sheet.independentBoardSeats;
                          return rankings[0].sheet.founderBoardSeats > total / 2
                            ? 'Founders maintain control'
                            : 'Shared/investor control';
                        })()}
                      </li>
                      <li>
                        Anti-Dilution:{' '}
                        {rankings[0].sheet.antiDilution === 'none'
                          ? 'None (best for founders)'
                          : rankings[0].sheet.antiDilution === 'broad-based'
                            ? 'Broad-based weighted average (standard)'
                            : rankings[0].sheet.antiDilution === 'narrow-based'
                              ? 'Narrow-based weighted average'
                              : 'Full ratchet (unfavorable)'}
                      </li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-3">
                      Remember: The best deal isn't always the highest valuation. Consider the full
                      package of terms, your relationship with the investor, and their value-add
                      beyond capital.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper component for comparison rows
function ComparisonRow({
  label,
  sheets,
  getValue,
  isHighlighted,
}: {
  label: string;
  sheets: TermSheet[];
  getValue: (sheet: TermSheet) => string;
  isHighlighted?: (sheet: TermSheet) => boolean;
}) {
  return (
    <tr className="border-b">
      <td className="p-3 font-medium text-sm sticky left-0 bg-background z-10">{label}</td>
      {sheets.map((sheet) => (
        <td
          key={sheet.id}
          className={`p-3 text-center text-sm ${isHighlighted?.(sheet) ? 'bg-success/10 font-semibold' : ''}`}
        >
          {getValue(sheet)}
        </td>
      ))}
    </tr>
  );
}
